import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  CopyButton,
  Drawer,
  Group,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCopy, IconDots, IconList, IconPencil, IconPlus, IconRefresh, IconSend, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteWebhook,
  useSaveWebhook,
  useWebhookAction,
  useWebhookDeliveries,
  useWebhookEvents,
  useWebhooks,
  type Webhook,
  type WebhookEvent,
} from '@/api/webhooks';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';
import dayjs from '@/lib/dayjs';
import { dateTimeFormat } from '@/lib/format';

/** Integrationen: Webhooks (API-04) und Hinweise zur REST-API (API-01/05). */
export function IntegrationsSettings() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  return (
    <Stack maw={1000}>
      {ctx.hasFeature('WEBHOOKS') ? <Webhooks /> : <Alert color="gray">{t('webhooks.notInPlan')}</Alert>}
      <Paper withBorder p="md">
        <Title order={5} mb="xs">
          {t('webhooks.apiTitle')}
        </Title>
        <Text size="sm" c="dimmed">
          {t('webhooks.apiText')}{' '}
          <Anchor href="/swagger-ui.html" target="_blank">
            /swagger-ui.html
          </Anchor>
        </Text>
      </Paper>
    </Stack>
  );
}

function Webhooks() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const onError = useApiError();
  const { data: hooks = [], isLoading } = useWebhooks(ctx.workspaceId);
  const remove = useDeleteWebhook(ctx.workspaceId);
  const action = useWebhookAction(ctx.workspaceId);
  const save = useSaveWebhook(ctx.workspaceId);
  const [editing, setEditing] = useState<Webhook | 'new' | null>(null);
  const [logOf, setLogOf] = useState<Webhook | null>(null);

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>{t('webhooks.title')}</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
          {t('webhooks.create')}
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        {t('webhooks.text')}
      </Text>
      <Table verticalSpacing="sm">
        <Table.Tbody>
          {hooks.map((h) => (
            <Table.Tr key={h.id}>
              <Table.Td>
                <Text fw={500}>{h.name}</Text>
                <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
                  {h.url}
                </Text>
                {h.disabledReason && (
                  <Text size="xs" c="red">
                    {h.disabledReason}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {h.events.slice(0, 3).map((e) => (
                    <Badge key={e} variant="light" size="sm">
                      {e}
                    </Badge>
                  ))}
                  {h.events.length > 3 && <Badge variant="outline" size="sm">+{h.events.length - 3}</Badge>}
                </Group>
              </Table.Td>
              <Table.Td>
                <Switch
                  checked={h.enabled}
                  onChange={(e) => save.mutate({ id: h.id, enabled: e.currentTarget.checked }, { onError })}
                  aria-label={t('webhooks.enabled')}
                />
              </Table.Td>
              <Table.Td ta="right">
                <Menu withinPortal position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" aria-label={t('common.more')}>
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditing(h)}>
                      {t('common.edit')}
                    </Menu.Item>
                    <Menu.Item leftSection={<IconList size={14} />} onClick={() => setLogOf(h)}>
                      {t('webhooks.deliveries')}
                    </Menu.Item>
                    <Menu.Item leftSection={<IconSend size={14} />} onClick={() => action.mutate({ id: h.id, action: 'ping' }, { onSuccess: () => setLogOf(h), onError })}>
                      {t('webhooks.ping')}
                    </Menu.Item>
                    <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => action.mutate({ id: h.id, action: 'rotate' }, { onError })}>
                      {t('webhooks.rotate')}
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={() =>
                        modals.openConfirmModal({
                          title: t('webhooks.deleteTitle'),
                          children: <Text size="sm">{t('webhooks.deleteText', { name: h.name })}</Text>,
                          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
                          confirmProps: { color: 'red' },
                          onConfirm: () => remove.mutate(h.id, { onError }),
                        })
                      }
                    >
                      {t('common.delete')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
          {!isLoading && hooks.length === 0 && (
            <Table.Tr>
              <Table.Td>
                <Text c="dimmed" size="sm">
                  {t('webhooks.empty')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      {editing && <WebhookModal hook={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <DeliveryLog hook={logOf} onClose={() => setLogOf(null)} />
    </Paper>
  );
}

function WebhookModal({ hook, onClose }: { hook: Webhook | null; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const onError = useApiError();
  const save = useSaveWebhook(ctx.workspaceId);
  const { data: events = [] } = useWebhookEvents(ctx.workspaceId);
  const [name, setName] = useState(hook?.name ?? '');
  const [url, setUrl] = useState(hook?.url ?? '');
  const [selected, setSelected] = useState<string[]>(hook?.events ?? ['time-entry.created', 'time-entry.updated', 'time-entry.deleted']);
  return (
    <Modal opened onClose={onClose} title={hook ? t('webhooks.edit') : t('webhooks.create')} size="lg">
      <Stack>
        <TextInput label={t('webhooks.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <TextInput label={t('webhooks.url')} placeholder="https://example.com/hooks/firetracker" value={url} onChange={(e) => setUrl(e.currentTarget.value)} required />
        <MultiSelect label={t('webhooks.events')} data={events} value={selected} onChange={setSelected} searchable />
        {hook && (
          <div>
            <Text size="sm" fw={500}>
              {t('webhooks.secret')}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Code>{hook.secret}</Code>
              <CopyButton value={hook.secret}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? t('common.copied') : t('apiKeys.copy')}>
                    <ActionIcon variant="subtle" onClick={copy} aria-label={t('apiKeys.copy')}>
                      <IconCopy size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>
              {t('webhooks.signatureHint')}
            </Text>
          </div>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!url.trim() || selected.length === 0}
            loading={save.isPending}
            onClick={() => save.mutate({ id: hook?.id, name, url, events: selected as WebhookEvent[] }, { onSuccess: onClose, onError })}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DeliveryLog({ hook, onClose }: { hook: Webhook | null; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const onError = useApiError();
  const { data: deliveries = [] } = useWebhookDeliveries(ctx.workspaceId, hook?.id ?? null);
  const action = useWebhookAction(ctx.workspaceId);
  const [open, setOpen] = useState<string | null>(null);
  const color = { SUCCESS: 'green', PENDING: 'yellow', FAILED: 'red' } as Record<string, string>;
  return (
    <Drawer opened={!!hook} onClose={onClose} position="right" size="lg" title={t('webhooks.deliveriesOf', { name: hook?.name ?? '' })}>
      <Stack gap="xs">
        {deliveries.length === 0 && <Text c="dimmed">{t('webhooks.noDeliveries')}</Text>}
        {deliveries.map((d) => (
          <Paper key={d.id} withBorder p="xs" onClick={() => setOpen(open === d.id ? null : d.id!)} style={{ cursor: 'pointer' }}>
            <Group justify="space-between">
              <Group gap="xs">
                <Badge color={color[d.status ?? ''] ?? 'gray'}>{d.status}</Badge>
                <Text size="sm" fw={500}>
                  {d.event}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {dayjs(d.createdAt).tz(ctx.prefs.timezone).format(dateTimeFormat(ctx.prefs, true))} · {t('webhooks.attempts', { count: d.attempts ?? 0 })}
                {d.responseStatus ? ` · HTTP ${d.responseStatus}` : ''}
              </Text>
            </Group>
            {d.error && (
              <Text size="xs" c="red">
                {d.error}
              </Text>
            )}
            {open === d.id && (
              <Stack gap={4} mt="xs">
                <Code block>{JSON.stringify(JSON.parse(d.payload ?? '{}'), null, 2)}</Code>
                {d.responseBody && <Code block>{d.responseBody}</Code>}
                {d.status !== 'PENDING' && (
                  <Button size="xs" variant="light" w="fit-content" onClick={(e) => {
                    e.stopPropagation();
                    action.mutate({ id: hook!.id, action: 'redeliver', deliveryId: d.id }, { onError });
                  }}>
                    {t('webhooks.redeliver')}
                  </Button>
                )}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>
    </Drawer>
  );
}
